import {Component, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {IFCCheck, IfcControllerService, Task} from '../../../../../api';
import {StateConfigService} from "../../../../../services/state/state-config.service";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {TaskDetailsService} from "../../../../../services/tasks/task-details.service";
import {faCircleCheck, faCircleExclamation, faCircleXmark} from "@fortawesome/pro-solid-svg-icons";
import {faSpinnerThird} from "@fortawesome/pro-duotone-svg-icons";
import {HttpClient} from '@angular/common/http';
import {DatePipe} from '@angular/common';
import {LodPipe} from '../../../../../pipes/lod.pipe';
import {LoiCheckStepperComponent} from '../loi-check-stepper/loi-check-stepper.component';
import {OverviewReportComponent} from '../../../overviewReport/overview-report.component';
import {
  BicResultColumnComponent,
  BicResultComponent,
  BicResultHeaderComponent,
  BicResultRowComponent
} from "@buildinginformationcloud/bic-ui-components";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {BimViewerService, TopicCreationData} from "../../../../../services/bim-viewer/bim-viewer.service";
import {roundPercentPreservingExtremes} from '../../../../../models/report/report-leaves';

export interface LoiElement {
  name: string
  count: number
  properties: LoiProperties[]
  rule_description?: string
  text?: string
  guid_fail_list?: string[]
}

export interface LoiProperties {
  name: string
  result: number
  guidListCorrect: string[]
  guidListFail: string[]
}

type ResponseType = 'success' | 'warning' | 'error';
type LoiResultItem = LoiElement | { score: number } | { error?: string };

@Component({
  selector: 'app-loi-check-reports',
  templateUrl: './loi-check-report.component.html',
  styleUrls: ['./loi-check-report.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LoiCheckStepperComponent,
    OverviewReportComponent,
    BicResultColumnComponent,
    BicResultComponent,
    BicResultHeaderComponent,
    BicResultRowComponent,
    BicResultColumnComponent,
    FaIconComponent
  ]
})
export class LoiCheckReportComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private stateConfigService = inject(StateConfigService);
  private taskDetailService = inject(TaskDetailsService);
  private http = inject(HttpClient);
  private datePipe = inject(DatePipe);
  private lodPipe = inject(LodPipe);
  private router = inject(Router);
  private bimViewerService = inject(BimViewerService);
  selectedStep = 0

  check!: IFCCheck
  loading = true;
  selectedRule: LoiElement | undefined
  dataSource: LoiElement[] = []
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faSpinnerThird = faSpinnerThird;

  score = 0
  allCount = 0
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faCircleXmark = faCircleXmark;

  percentCorrectProperty(property: LoiProperties): number {
    let value = property.guidListCorrect.length / (property.guidListCorrect.length + property.guidListFail.length) * 100

    return isNaN(value) ? 0 : roundPercentPreservingExtremes(value)
  }

  private ifcControllerService = inject(IfcControllerService);

  get rulesOfSelectedProjectFile(): LoiElement[] {
    return this.dataSource
  }

  get resultOfSelectedProjectFileAndSelectedRule(): Array<{
    type: ResponseType;
    responseText: string;
    percentage: number
  }> {
    if (!this.selectedRule) {
      return []
    }

    return this.selectedRule.properties.map(property => {
      let type: ResponseType = 'success'
      if (property.result < 1) {
        type = 'error'
      }

      return {
        type: type,
        responseText: property.name,
        percentage: this.percentCorrectProperty(property)
      }
    })
  }

  get checkerName(): string {
    return this.check.checker?.firstName ? this.check.checker.firstName + ' ' + this.check.checker.lastName : 'Unbekannt'
  }

  get fileSize(): string {
    if (this.check.projectFile?.size) {
      const sizeInMB = this.check.projectFile.size / (1024 * 1024);
      return sizeInMB.toFixed(2) + ' MB';
    } else {
      return 'Unbekannt'
    }
  }

  get fileName(): string {
    return this.check.projectFile?.name ?? 'Unbekannt'
  }

  get ruleName(): string {
    if (this.check.loiCheckRule?.name) {
      return this.check.loiCheckRule.name
    } else if (this.check.lodType) {
      return this.lodPipe.transform(this.check.lodType) ?? 'Unbekannt'
    } else {
      return 'Unbekannt'
    }
  }

  medianOfPercentages(): number {
    let filteredData = this.dataSource.filter(element => element.count > 0);
    let totalElements = filteredData.reduce((total, element) => total + element.count, 0);
    let value = filteredData.map(element => this.medianOfPercentagesElement(element) * element.count).reduce((a, b) => a + b, 0) / totalElements;

    return isNaN(value) ? 0 : roundPercentPreservingExtremes(value)
  }

  medianOfPercentagesElement(element: LoiElement): number {
    let value = (element.properties ?? []).map(property => this.percentCorrectProperty(property)).reduce((a, b) => a + b, 0) / (element.properties ?? []).length

    return isNaN(value) ? 0 : roundPercentPreservingExtremes(value)
  }

  statusForRuleAndSelectedFile(rule: LoiElement): ResponseType {
    const resultValues = rule.properties.map(property => property.result)

    // Wenn ein Wert unter 0.5 ist = error
    if (resultValues.some(val => val < 0.5)) {
      return 'error'
    }
    // Wenn ein Wert unter 1.0 ist = warning
    if (resultValues.some(val => val < 1.0)) {
      return 'warning'
    }
    return 'success'
  }

  resultPercentageForRuleAndSelectedFile(rule: LoiElement): number {
    const totalResults = rule.properties.length
    const totalPercentage = rule.properties.reduce((sum, property) => sum + property.result, 0)

    // Vorher Math.floor, waehrend percentCorrectProperty() rundete: eine Eigenschaft bei 99,6 %
    // zeigte 100 %, die Regel darueber 99 %. Beide Ebenen runden jetzt identisch.
    return totalResults === 0 ? 0 : roundPercentPreservingExtremes((totalPercentage / totalResults) * 100)
  }

  countForRuleAndSelectedFile(rule: LoiElement): number {
    return rule.count;
  }

  statusIcon(type: ResponseType) {
    if (type === 'success') {
      return this.faCircleCheck
    }
    if (type === 'warning') {
      return this.faCircleExclamation
    }
    return this.faCircleXmark
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      if (params.reportId !== undefined) {
        this.check = {id: params.reportId}
      }

      this.ifcControllerService.getCheck1(this.check.id!, this.stateConfigService.getProjectId()).subscribe(check => {
        this.check = check;

        const results = this.parseResults();
        const scoreEntry = results.find((item): item is { score: number } => 'score' in item);

        this.score = (scoreEntry?.score ?? 0) * 100
        this.dataSource = results.filter(this.isLoiElement)
        this.allCount = this.dataSource.reduce((total, element) => total + element.count, 0)
        this.loading = false;
      });
    })
  }

  createTaskForRuleAndSelectedFile(rule: LoiElement) {
    const fileName = this.check.projectFile?.name ?? ''
    let task = {
      markup: {
        topic: {
          title: 'Überarbeiten notwendig für: ' + fileName + ' bezüglich Regel: ' + rule.name,
        }
      },
    }

    this.taskDetailService.setOpenedTask(task as Task);
  }

  createTaskForProperty(result: { type: ResponseType; responseText: string; percentage: number }) {
    const fileName = this.check.projectFile?.name ?? ''
    const ruleName = this.selectedRule?.name ?? ''
    const task = {
      markup: {
        topic: {
          title: `Überarbeiten notwendig für: ${fileName} bezüglich Attribut: ${result.responseText} (${ruleName})`,
        }
      },
    }

    this.taskDetailService.setOpenedTask(task as Task);
  }

  downloadReport() {
    const formattedDate = `${this.datePipe.transform(this.check.checkDate, 'dd.MM.y')} - ${this.datePipe.transform(this.check.checkDate, 'HH:mm')}`;
    let payload = {
      json_data: this.dataSource,
      template_path: 'https://de-thinkbic-public.s3.eu-central-1.amazonaws.com/bericht_loi_check_template.xlsx',
      target_texts: {
        'Prüfung durchgeführt am:': formattedDate,
        'Prüfregelname:': this.check.lodType ? this.lodPipe.transform(this.check.lodType) : this.check.loiCheckRule?.name,
        'Geprüftes Modell:': this.check?.projectFile?.name?.split('.').slice(0, -1).join('.') ?? '',
        'Geprüfte Bauteile:': this.allCount,
        'Gesamtergebnis:': this.score.toString() + '%'
      }
      // target_texts: {
      //   "Prüfung durchgeführt am:": "Test",
      //   "Prüfregelname:": "Test",
      //   "Geprüftes Modell:": "Test",
      //   "Geprüfte Bauteile:": "Test",
      //   "Gesamtergebnis:": "Test"
      // }
    }
    this.http.post('https://services.thinkbic.de/bim/fill-loi-template', payload,
      {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token',) || ''}`
        }
      }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bericht_loi_check.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  }

  private parseResults(): LoiResultItem[] {
    return JSON.parse(this.check.results ?? '[]');
  }

  private isLoiElement(result: LoiResultItem): result is LoiElement {
    return typeof (result as LoiElement).name === 'string' && Array.isArray((result as LoiElement).properties)
  }

  navigateToBaseCheckReport() {
    this.router.navigate(['base-check'], {relativeTo: this.activatedRoute.parent})
  }

  /** Erstellt beim Öffnen im Viewer ein BCF-Topic pro fehlgeschlagener Eigenschaft. */
  openPressed() {

    const topicInputs: TopicCreationData[] = []

    this.dataSource.forEach((rule: LoiElement) => {
      const failedProperties = rule.properties.filter(property => property.guidListFail.length > 0);

      failedProperties.forEach(property => {
        const propertyName = property.name?.trim() || 'Anforderung';
        const sentence = property.guidListCorrect.length === 0
          ? 'kein Bauteil erfüllt'
          : `${property.guidListCorrect.length}/${property.guidListCorrect.length + property.guidListFail.length} Bauteile erfüllt`;

        const descriptionParts = [`${propertyName}: ${sentence}`];
        if (rule.rule_description) descriptionParts.push(rule.rule_description);
        if (rule.text) descriptionParts.push(rule.text);

        topicInputs.push({
          title: `${rule.name}: ${propertyName}`,
          description: descriptionParts.join('\n\n'),
          guids: [...property.guidListFail],
          labels: ['LOI-Check'],
          specification: rule.name,
          requirement: propertyName,
        });
      });
    })

    this.bimViewerService.open(this.check.projectFile!, topicInputs);
  }
}
